import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Plus, X, ArrowRight, Tv, RotateCcw, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { useEstruturaSessao } from '../components/EstruturaSessao'; // PLENARIUM:QUORUM_AJUSTE
import { excluirComConfirmacao } from '../components/ConfirmarExclusao'; // PLENARIUM:LIXEIRA

const STATUS_LABEL = { AGENDADA: 'Agendada', EM_ANDAMENTO: 'Em Andamento', ENCERRADA: 'Encerrada' };
const STATUS_BADGE = { AGENDADA: 'badge-amarelo', EM_ANDAMENTO: 'badge-vermelho', ENCERRADA: 'badge-cinza' };

export default function Sessoes() {
  const { usuario } = useAuth();
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const estQ = useEstruturaSessao(); // PLENARIUM:QUORUM_AJUSTE -- o quorum vem da Estrutura da camara
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ titulo: '', data: '', tipoQuorum: 'MAIORIA_ABSOLUTA' });
  const [salvando, setSalvando] = useState(false);

  const carregar = async () => {
    setLoading(true);
    try { const r = await axios.get('/api/sessoes'); setLista(r.data); }
    catch { toast.error('Erro ao carregar sessões'); }
    finally { setLoading(false); }
  };

  useEffect(() => { carregar(); }, []);

  const salvar = async (e) => {
    e.preventDefault();
    setSalvando(true);
    try {
      await axios.post('/api/sessoes', { ...form, camaraId: usuario?.camaraId });
      toast.success('Sessão criada!');
      setModal(false);
      setForm({ titulo: '', data: '', tipoQuorum: 'MAIORIA_ABSOLUTA' });
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao criar sessão');
    } finally { setSalvando(false); }
  };

  const reabrir = async (id, titulo) => {
    if (!window.confirm(`Reabrir a sessão "${titulo}"?`)) return;
    try {
      await axios.patch(`/api/sessoes/${id}/reabrir`);
      toast.success('Sessão reaberta!');
      carregar();
    } catch { toast.error('Erro ao reabrir sessão'); }
  };

  // PLENARIUM:LIXEIRA -- sessao realizada (ou com votos/presencas) pede motivo + senha; copia vai para a lixeira
  const excluir = async (id, titulo) => {
    if (!window.confirm(`Excluir a sessão "${titulo}"? Pautas, votos e presenças dela serão removidos.`)) return;
    try {
      if (await excluirComConfirmacao(`/api/sessoes/${id}`)) { toast.success('Sessão excluída.'); carregar(); }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao excluir sessão');
    }
  };
  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Sessões</h1>
          <p className="page-subtitle">{lista.length} sessão(ões) cadastrada(s)</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal(true)}>
          <Plus size={15} /> Nova Sessão
        </button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="loading-screen" style={{ minHeight: 220 }}><div className="spinner" /></div>
        ) : lista.length === 0 ? (
          <div className="sem-dados"><p>Nenhuma sessão cadastrada.</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Título</th>
                  <th>Data</th>
                  <th>Pautas</th>
                  {/* PLENARIUM:QUORUM_AJUSTE -- coluna Quorum removida */}
                  <th>Status</th>
                  <th style={{ width: 240 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {lista.map(s => (
                  <tr key={s.id}>
                    <td><span style={{ fontWeight: 600 }}>{s.titulo}</span></td>
                    <td style={{ color: 'var(--texto-suave)', fontSize: '0.8rem' }}>
                      {format(new Date(new Date(s.data).getTime() + new Date(s.data).getTimezoneOffset() * 60000), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}{/* PLENARIUM:AJUSTES_2110B -- mostra a hora como foi cadastrada */}
                    </td>
                    <td>
                      <span className="badge badge-cinza">{s.pautas?.length ?? 0} pauta(s)</span>
                    </td>
                    {/* PLENARIUM:QUORUM_AJUSTE -- selo de quorum removido */}
                    <td>
                      <span className={`badge ${STATUS_BADGE[s.status]}`}>
                        {STATUS_LABEL[s.status]}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <Link to={`/sessoes/${s.id}`} className="btn btn-secondary btn-sm">
                          <ArrowRight size={13} /> Gerenciar
                        </Link>
                        <Link to={`/tv/${s.id}?camaraId=${usuario?.camaraId || ''}`} target="_blank" className="btn btn-primary btn-sm">
                          <Tv size={13} /> TV
                        </Link>
                        {s.status === 'ENCERRADA' && (
                          <button className="btn btn-warning btn-sm" onClick={() => reabrir(s.id, s.titulo)}>
                            <RotateCcw size={13} /> Reabrir
                          </button>
                        )}
                        <button className="btn btn-danger btn-sm" onClick={() => excluir(s.id, s.titulo)}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Nova Sessão</h2>
              <button className="btn-modal-close" onClick={() => setModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={salvar}>
              <div className="form-group">
                <label className="form-label">Título da Sessão *</label>
                <input
                  className="form-input"
                  value={form.titulo}
                  onChange={e => setForm({ ...form, titulo: e.target.value })}
                  placeholder="Ex: 1ª Sessão Ordinária de Março/2026"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Data e Hora *</label>
                <input
                  className="form-input"
                  type="datetime-local"
                  value={form.data}
                  onChange={e => setForm({ ...form, data: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Quórum desta câmara</label>
                <div style={{ fontSize: '0.86rem', padding: '9px 12px', borderRadius: 8, background: 'var(--fundo-suave, #f1f5f9)' }}>
                  Para abrir a sessão: <b>{estQ.quorumAbertura ? `${estQ.quorumAbertura} vereadores` : 'maioria absoluta'}</b> · Para as votações: <b>{estQ.quorumVotacao ? `${estQ.quorumVotacao} vereadores` : 'maioria absoluta'}</b>
                </div>
                <small style={{ color: 'var(--texto-suave)', fontSize: '0.76rem' }}>Vale para todas as sessões. Para alterar, use o botão “Estrutura” na condução da sessão.</small>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={salvando}>
                  {salvando ? 'Criando...' : 'Criar Sessão'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
